import React from "react";
import { type RouteObject } from "react-router-dom";
import { BillsPage } from "@pages/BillsPage/BillsPage";
import { BudgetActionBarSkeleton } from "@pages/BudgetPage/components/BudgetActionBarSkeleton";
import { InternalNotFoundPage } from "@pages/InternalNotFoundPage/InternalNotFoundPage";
import { BudgetSkeleton } from "@features/Budget/BudgetSkeleton";
import { DefaultPageSkeleton } from "@features/DefaultPageSkeleton/DefaultPageSkeleton";
import { ReferralPageSkeleton } from "@features/Referral/components/ReferralPageSkeleton/ReferralPageSkeleton";
import { AdditionalServiceSkeleton } from "@features/skeletons/AdditionalServices/AdditionalServiceSkeleton/AdditionalServiceSkeleton";
import { AdditionalServicesSkeleton } from "@features/skeletons/AdditionalServices/AdditionalServicesSkeleton/AdditionalServicesSkeleton";
import { ArchiveActionBarSkeleton } from "@features/skeletons/Archive/ArchiveActionBarSkeleton/ArchiveActionBarSkeleton";
import { ArchiveSkeleton } from "@features/skeletons/Archive/ArchiveSkeleton/ArchiveSkeleton";
import { BillsPageSkeleton } from "@features/skeletons/Bills/BillsPageSkeleton/BillsPageSkeleton";
import { JointBillTableSkeleton } from "@features/skeletons/Bills/JointBillTableSkeleton/JointBillTableSkeleton";
import { PageLayoutSkeleton } from "@features/skeletons/common/PageLayoutSkeleton/PageLayoutSkeleton";
import { CustomerStorageSkeleton } from "@features/skeletons/CustomerStorage/CustomerStorageSkeleton/CustomerStorageSkeleton";
import { MessagesPageSkeleton } from "@features/skeletons/Messages/MessagesPageSkeleton/MessagesPageSkeleton";
import { ErrorBoundary } from "@components/Error/ErrorBoundary";
import { InternalLayout } from "@components/layouts/InternalLayout/InternalLayout";

const InternalIndexPage = React.lazy(() =>
  import("@pages/InternalIndexPage/InternalIndexPage").then((module) => ({
    default: module.InternalIndexPage,
  })),
);

const OverviewPage = React.lazy(() =>
  import("@pages/OverviewPage/OverviewPage").then((module) => ({
    default: module.OverviewPage,
  })),
);

const MessagesPage = React.lazy(() =>
  import("@pages/MessagesPage/MessagesPage").then((module) => ({
    default: module.MessagesPage,
  })),
);

const ArchiveListPage = React.lazy(() =>
  import("@pages/ArchiveListPage/ArchiveListPage").then((module) => ({
    default: module.ArchiveListPage,
  })),
);

const ArchiveJointBillPage = React.lazy(() =>
  import("@pages/ArchiveJointBillPage/ArchiveJointBillPage").then((module) => ({
    default: module.ArchiveJointBillPage,
  })),
);

const AdditionalServicesPage = React.lazy(() =>
  import("@pages/AdditionalServicesPage/AdditionalServicesPage").then(
    (module) => ({
      default: module.AdditionalServicesPage,
    }),
  ),
);

const AdditionalServicePage = React.lazy(() =>
  import("@pages/AdditionalServicePage/AdditionalServicePage").then(
    (module) => ({
      default: module.AdditionalServicePage,
    }),
  ),
);

const PlansPage = React.lazy(() =>
  import("@pages/PlansPage/PlansPage").then((module) => ({
    default: module.PlansPage,
  })),
);

const SettingsPage = React.lazy(() =>
  import("@pages/SettingsPage/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);

const StatisticsPage = React.lazy(() =>
  import("@pages/StatisticsPage/StatisticsPage").then((module) => ({
    default: module.StatisticsPage,
  })),
);

const StatisticsBreakdownPage = React.lazy(() =>
  import("@pages/StatisticsBreakdownPage/StatisticsBreakdownPage").then(
    (module) => ({
      default: module.StatisticsBreakdownPage,
    }),
  ),
);

const ReferralPage = React.lazy(() =>
  import("@pages/ReferralPage/ReferralPage").then((module) => ({
    default: module.ReferralPage,
  })),
);

const HelpPage = React.lazy(() =>
  import("@pages/HelpPage/HelpPage").then((module) => ({
    default: module.HelpPage,
  })),
);

const BudgetPage = React.lazy(() =>
  import("@pages/BudgetPage/BudgetPage").then((module) => ({
    default: module.BudgetPage,
  })),
);

const BudgetBreakdownPage = React.lazy(() =>
  import("@pages/BudgetBreakdownPage/BudgetBreakdownPage").then((module) => ({
    default: module.BudgetBreakdownPage,
  })),
);

const BudgetExpensePlanningPage = React.lazy(() =>
  import("@pages/BudgetExpensePlanningPage/BudgetExpensePlanningPage").then(
    (module) => ({
      default: module.BudgetExpensePlanningPage,
    }),
  ),
);

const BudgetSavingsPlanningPage = React.lazy(() =>
  import("@pages/BudgetSavingsPlanningPage/BudgetSavingsPlanningPage").then(
    (module) => ({
      default: module.BudgetSavingsPlanningPage,
    }),
  ),
);

const CustomerStoragePage = React.lazy(() =>
  import("@pages/CustomerStoragePage/CustomerStoragePage").then((module) => ({
    default: module.CustomerStoragePage,
  })),
);

const OndatoVerificationPage = React.lazy(() =>
  import("@pages/OndatoVerificationPage/OndatoVerificationPage").then(
    (module) => ({
      default: module.OndatoVerificationPage,
    }),
  ),
);

export const internalRoutes: RouteObject = {
  element: <InternalLayout />,
  children: [
    {
      errorElement: <ErrorBoundary showTopBar />,
      children: [
        {
          path: "*",
          element: <InternalNotFoundPage />,
        },
        {
          path: "/",
          element: (
            <React.Suspense fallback={<></>}>
              <InternalIndexPage />
            </React.Suspense>
          ),
        },
        {
          path: "/overview",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <OverviewPage />
            </React.Suspense>
          ),
        },
        {
          path: "/bills",
          element: (
            <React.Suspense fallback={<BillsPageSkeleton />}>
              <BillsPage />
            </React.Suspense>
          ),
        },
        {
          path: "/bills/:periodId",
          element: (
            <React.Suspense fallback={<BillsPageSkeleton />}>
              <BillsPage />
            </React.Suspense>
          ),
        },
        {
          path: "/messages",
          element: (
            <React.Suspense fallback={<MessagesPageSkeleton />}>
              <MessagesPage />
            </React.Suspense>
          ),
        },
        {
          path: "/messages/:id",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <MessagesPage />
            </React.Suspense>
          ),
        },
        {
          path: "/archive",
          element: (
            <React.Suspense
              fallback={
                <PageLayoutSkeleton
                  actionBarSkeleton={<ArchiveActionBarSkeleton />}
                >
                  <ArchiveSkeleton />
                </PageLayoutSkeleton>
              }
            >
              <ArchiveListPage />
            </React.Suspense>
          ),
        },
        {
          path: "/archive/:periodId",
          element: (
            <React.Suspense
              fallback={
                <PageLayoutSkeleton
                  actionBarSkeleton={<ArchiveActionBarSkeleton />}
                >
                  <JointBillTableSkeleton />
                </PageLayoutSkeleton>
              }
            >
              <ArchiveJointBillPage />
            </React.Suspense>
          ),
        },
        {
          path: "/services",
          element: (
            <React.Suspense
              fallback={
                <PageLayoutSkeleton>
                  <div className="px-4 md:px-0">
                    <AdditionalServicesSkeleton />
                  </div>
                </PageLayoutSkeleton>
              }
            >
              <AdditionalServicesPage />
            </React.Suspense>
          ),
        },
        {
          path: "/services/ordered",
          element: (
            <React.Suspense
              fallback={
                <PageLayoutSkeleton>
                  <div className="px-4 md:px-0">
                    <AdditionalServicesSkeleton count={3} />
                  </div>
                </PageLayoutSkeleton>
              }
            >
              <AdditionalServicesPage isOrderedPage />
            </React.Suspense>
          ),
        },
        {
          path: "/services/:id",
          element: (
            <React.Suspense
              fallback={
                <PageLayoutSkeleton>
                  <div className="px-4 md:px-0">
                    <AdditionalServiceSkeleton />
                  </div>
                </PageLayoutSkeleton>
              }
            >
              <AdditionalServicePage />
            </React.Suspense>
          ),
        },
        {
          path: "/plans",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <PlansPage />
            </React.Suspense>
          ),
        },
        {
          path: "/referral",
          element: (
            <React.Suspense fallback={<ReferralPageSkeleton />}>
              <ReferralPage />
            </React.Suspense>
          ),
        },
        {
          path: "/settings",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <SettingsPage />
            </React.Suspense>
          ),
        },
        {
          path: "/settings/house",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <SettingsPage />
            </React.Suspense>
          ),
        },
        {
          path: "/settings/budget",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <SettingsPage />
            </React.Suspense>
          ),
        },
        {
          path: "/settings/permissions",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <SettingsPage />
            </React.Suspense>
          ),
        },
        {
          path: "/statistics",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <StatisticsPage />
            </React.Suspense>
          ),
        },
        {
          path: "/statistics/breakdown",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <StatisticsBreakdownPage />
            </React.Suspense>
          ),
        },
        {
          path: "/help/:category?",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <HelpPage />
            </React.Suspense>
          ),
        },
        {
          path: "/budget",
          element: (
            <React.Suspense
              fallback={
                <PageLayoutSkeleton
                  actionBarSkeleton={<BudgetActionBarSkeleton />}
                >
                  <BudgetSkeleton />
                </PageLayoutSkeleton>
              }
            >
              <BudgetPage />
            </React.Suspense>
          ),
        },
        {
          path: "/budget/breakdown",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <BudgetBreakdownPage />
            </React.Suspense>
          ),
        },
        {
          path: "/budget/expenses/:state/:expensePlanId?",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <BudgetExpensePlanningPage />
            </React.Suspense>
          ),
        },
        {
          path: "/budget/savings/:state/:savingsPlanId?",
          element: (
            <React.Suspense fallback={<DefaultPageSkeleton />}>
              <BudgetSavingsPlanningPage />
            </React.Suspense>
          ),
        },
        {
          path: "/storage",
          element: (
            <React.Suspense
              fallback={
                <PageLayoutSkeleton>
                  <div className="px-4 md:px-0">
                    <CustomerStorageSkeleton />
                  </div>
                </PageLayoutSkeleton>
              }
            >
              <CustomerStoragePage />
            </React.Suspense>
          ),
        },
        {
          path: "/storage/:folderUrl?",
          element: (
            <React.Suspense
              fallback={
                <PageLayoutSkeleton>
                  <div className="px-4 md:px-0">
                    <CustomerStorageSkeleton />
                  </div>
                </PageLayoutSkeleton>
              }
            >
              <CustomerStoragePage />
            </React.Suspense>
          ),
        },
        {
          path: "/verification/od/:verificationStatus",
          element: (
            <React.Suspense fallback={null}>
              <OndatoVerificationPage />
            </React.Suspense>
          ),
        },
      ],
    },
  ],
};
